import React, { useContext } from 'react';
import {
  filterNotUndefinedOrNull,
  mapNotUndefinedOrNull,
} from '../../util/listutils';
import {
  PraksisavtaleVedleggDto,
  PraksisavtaleVedleggMinimalDto as UHPraksisavtaleVedleggMinimalDto,
  PraksisavtaleVedleggTypeDto,
} from '../../api/generated/studiested-generated-api';
import { PraksisavtaleVedleggMinimalDto as PSPraksisavtaleVedleggMinimalDto } from '../../api/generated/praksissted-generated-api';
import {
  AgreementStateAction,
  AgreementStateActionType,
  useAgreementContext,
} from './index';
import { StudiestedApiContext } from '../../api/StudiestedApiContext';

export type FileFromFrontend = {
  file: File;
};

export type FileFromBackend = {
  minimal: UHPraksisavtaleVedleggMinimalDto;
  withTicket?: PraksisavtaleVedleggDto;
};

export type VedleggFileFromFrontend = {
  fileFromBackend?: never;
  fileFromFrontend: FileFromFrontend;
};

export type VedleggFileFromBackend = {
  fileFromBackend: FileFromBackend;
  fileFromFrontend?: never;
};

export type VedleggFile = VedleggFileFromBackend | VedleggFileFromFrontend;

export type Vedlegg<T extends PraksisavtaleVedleggTypeDto> = {
  id: string;
  type: T;
  vedleggFile: VedleggFile;
};

export type Avtalevedlegg = {
  samarbeidsavtale:
    | Vedlegg<typeof PraksisavtaleVedleggTypeDto.Samarbeidsavtale>
    | undefined;
  databehandleravtale:
    | Vedlegg<typeof PraksisavtaleVedleggTypeDto.Databehandleravtale>
    | undefined;
  annet: Vedlegg<typeof PraksisavtaleVedleggTypeDto.Annet>[];
};

export type VedleggResponse = {
  prevId: string;
  newVedlegg: UHPraksisavtaleVedleggMinimalDto;
};

type VedleggForDeletion = {
  avtaleId: string;
  vedleggId: string;
};

export const mapToVedlegg = <T extends PraksisavtaleVedleggTypeDto>(
  vedlegg: UHPraksisavtaleVedleggMinimalDto,
  type: T,
): Vedlegg<T> => ({
  type,
  id: vedlegg.id,
  vedleggFile: {
    fileFromBackend: { minimal: vedlegg },
  },
});

export const findSingleByTypeIn = <
  T extends
    | typeof PraksisavtaleVedleggTypeDto.Samarbeidsavtale
    | typeof PraksisavtaleVedleggTypeDto.Databehandleravtale,
>(
  vedleggList: UHPraksisavtaleVedleggMinimalDto[],
  type: T,
): Vedlegg<T> | undefined => {
  const minimalVedlegg = vedleggList.find((vedlegg) => vedlegg.type === type);

  return minimalVedlegg ? mapToVedlegg(minimalVedlegg, type) : undefined;
};

export const filterAnnetVedlegg = (
  vedleggList: UHPraksisavtaleVedleggMinimalDto[],
): Vedlegg<typeof PraksisavtaleVedleggTypeDto.Annet>[] =>
  mapNotUndefinedOrNull(
    vedleggList.filter(
      (vedlegg) => vedlegg.type === PraksisavtaleVedleggTypeDto.Annet,
    ),
    (vedlegg) => mapToVedlegg(vedlegg, PraksisavtaleVedleggTypeDto.Annet),
  );

export const mapToAvtaleVedlegg = (
  vedleggList: (
    | UHPraksisavtaleVedleggMinimalDto
    | PSPraksisavtaleVedleggMinimalDto
  )[],
): Avtalevedlegg => ({
  samarbeidsavtale: findSingleByTypeIn(
    vedleggList,
    PraksisavtaleVedleggTypeDto.Samarbeidsavtale,
  ),
  databehandleravtale: findSingleByTypeIn(
    vedleggList,
    PraksisavtaleVedleggTypeDto.Databehandleravtale,
  ),
  annet: filterAnnetVedlegg(vedleggList),
});

export const addNewVedlegg = (
  currentVedlegg: Avtalevedlegg,
  newVedlegg: Vedlegg<PraksisavtaleVedleggTypeDto>,
): Avtalevedlegg => {
  switch (newVedlegg.type) {
    case PraksisavtaleVedleggTypeDto.Samarbeidsavtale:
      return {
        ...currentVedlegg,
        samarbeidsavtale: newVedlegg as Vedlegg<
          typeof PraksisavtaleVedleggTypeDto.Samarbeidsavtale
        >,
      };
    case PraksisavtaleVedleggTypeDto.Databehandleravtale:
      return {
        ...currentVedlegg,
        databehandleravtale: newVedlegg as Vedlegg<
          typeof PraksisavtaleVedleggTypeDto.Databehandleravtale
        >,
      };
    case PraksisavtaleVedleggTypeDto.Annet:
      return {
        ...currentVedlegg,
        annet: [
          ...currentVedlegg.annet,
          newVedlegg as Vedlegg<typeof PraksisavtaleVedleggTypeDto.Annet>,
        ],
      };
  }
};

const replaceVedleggWithNew = (
  prevAnnet: Vedlegg<typeof PraksisavtaleVedleggTypeDto.Annet>[],
  newVedlegg: Vedlegg<PraksisavtaleVedleggTypeDto>,
): Vedlegg<typeof PraksisavtaleVedleggTypeDto.Annet>[] =>
  prevAnnet.reduce(
    (
      acc: Vedlegg<typeof PraksisavtaleVedleggTypeDto.Annet>[],
      vedlegg: Vedlegg<typeof PraksisavtaleVedleggTypeDto.Annet>,
    ) => {
      if (vedlegg.id !== newVedlegg.id) {
        return [...acc, vedlegg];
      }

      return [
        ...acc,
        newVedlegg as Vedlegg<typeof PraksisavtaleVedleggTypeDto.Annet>,
      ];
    },
    [],
  );

export const decorateVedleggWithExtraInfoFromBackend = (
  prevVedlegg: Avtalevedlegg,
  newVedlegg: Vedlegg<PraksisavtaleVedleggTypeDto>,
): Avtalevedlegg => {
  switch (newVedlegg.type) {
    case PraksisavtaleVedleggTypeDto.Samarbeidsavtale:
    case PraksisavtaleVedleggTypeDto.Databehandleravtale:
      return addNewVedlegg(prevVedlegg, newVedlegg);
    case PraksisavtaleVedleggTypeDto.Annet: {
      const { annet } = prevVedlegg;

      return {
        ...prevVedlegg,
        annet: replaceVedleggWithNew(annet, newVedlegg),
      };
    }
  }
};

export const removeVedleggByIdFromState = (
  currentVedlegg: Avtalevedlegg,
  id: string,
): Avtalevedlegg => {
  if (currentVedlegg.samarbeidsavtale?.id === id) {
    return { ...currentVedlegg, samarbeidsavtale: undefined };
  } else if (currentVedlegg.databehandleravtale?.id === id) {
    return { ...currentVedlegg, databehandleravtale: undefined };
  }

  return {
    ...currentVedlegg,
    annet: currentVedlegg.annet.filter((vedlegg) => vedlegg.id !== id),
  };
};

export const dispatchTicketAndExtendedVedleggInfo = (
  prevVedlegg: Vedlegg<PraksisavtaleVedleggTypeDto>,
  vedleggDto: PraksisavtaleVedleggDto,
  dispatch: React.Dispatch<AgreementStateAction>,
) =>
  dispatch({
    type: AgreementStateActionType.DECORATE_VEDLEGG_WITH_EXTRA_INFO_FROM_BACKEND,
    payload: {
      ...prevVedlegg,
      vedleggFile: {
        fileFromBackend: {
          ...prevVedlegg.vedleggFile.fileFromBackend!,
          withTicket: vedleggDto,
        },
      },
    },
  });

const flattenVedlegg = (
  vedlegg: Avtalevedlegg,
): Vedlegg<PraksisavtaleVedleggTypeDto>[] => {
  const { samarbeidsavtale, databehandleravtale, annet } = vedlegg;

  return filterNotUndefinedOrNull([
    samarbeidsavtale,
    databehandleravtale,
    ...annet,
  ]);
};

const initVedleggHasBeenRemoved = (
  avtaleId: string,
  initAvtaleVedlegg: Vedlegg<PraksisavtaleVedleggTypeDto>[],
  currentAvtaleVedleggIds: string[],
): VedleggForDeletion[] =>
  initAvtaleVedlegg
    .filter((vedlegg) => !currentAvtaleVedleggIds.includes(vedlegg.id))
    .map((vedlegg) => ({ avtaleId, vedleggId: vedlegg.id }));

/**
 * Backend will replace PraksisavtaleVedleggTypeDto.Samarbeidsavtale and
 * PraksisavtaleVedleggTypeDto.Databehandleravtale automatically, but
 * PraksisavtaleVedleggTypeDto.Annet needs to be handled explicitly.
 * */
const filterInitVedleggForDeletion = (
  avtaleId: string,
  initVedlegg: Avtalevedlegg,
  currentVedlegg: Avtalevedlegg,
): VedleggForDeletion[] => {
  const currentAvtaleVedleggIds = flattenVedlegg(currentVedlegg).map(
    (vedlegg) => vedlegg.id,
  );

  const {
    samarbeidsavtale: initSamarbeidsavtale,
    databehandleravtale: initDatabehandleravtale,
    annet: initAnnet,
  } = initVedlegg;
  const {
    samarbeidsavtale: currentSamarbeidsavtale,
    databehandleravtale: currentDatabehandleravtale,
  } = currentVedlegg;

  // Samarbeids-/databehandleravtale is overwritten by POST automatically, if a new version is posted.
  // We need only handle case of deletion without replacement
  const vedleggNotOverwrittenByPost = [
    ...filterNotUndefinedOrNull<
      Vedlegg<PraksisavtaleVedleggTypeDto> | undefined,
      Vedlegg<PraksisavtaleVedleggTypeDto>
    >([
      initSamarbeidsavtale && !currentSamarbeidsavtale
        ? initSamarbeidsavtale
        : undefined,
      initDatabehandleravtale && !currentDatabehandleravtale
        ? initDatabehandleravtale
        : undefined,
    ]),
    ...initAnnet,
  ];

  return initVedleggHasBeenRemoved(
    avtaleId,
    vedleggNotOverwrittenByPost,
    currentAvtaleVedleggIds,
  );
};

const responseIsUHPraksisavtaleVedleggMinimalDto = (
  response: unknown,
): response is UHPraksisavtaleVedleggMinimalDto => {
  if (!response || typeof response !== 'object') return false;
  const responseAsUHPraksisavtaleVedleggMinimalDto =
    response as UHPraksisavtaleVedleggMinimalDto;

  return (
    responseAsUHPraksisavtaleVedleggMinimalDto.type in
      PraksisavtaleVedleggTypeDto &&
    responseAsUHPraksisavtaleVedleggMinimalDto.navn !== undefined &&
    responseAsUHPraksisavtaleVedleggMinimalDto.id !== undefined &&
    responseAsUHPraksisavtaleVedleggMinimalDto.resourceLink !== undefined
  );
};

const usePostVedleggRequest =
  (): ((
    avtaleId: string,
    type: PraksisavtaleVedleggTypeDto,
    fileFromFrontend: FileFromFrontend,
  ) => Promise<Response>) =>
  (avtaleId: string, type, fileFromFrontend) => {
    const formData = new FormData();
    formData.append('type', type);
    formData.append('navn', fileFromFrontend.file.name);
    formData.append('vedlegg', fileFromFrontend.file);

    /* TODO When mustache templates are up to date, regarding multipart/form-data
    avtaleApi.avtalerPraksisAvtaleIdVedleggPost({
      pathParams: { avtaleId },
      formParams: {
        type,
        navn: fileFromFrontend.file.name,
        vedlegg: fileFromFrontend.file,
      },
    });
    */

    return fetch(
      `${window.location.origin}/studiestedapi/avtaler/praksis/${avtaleId}/vedlegg`,
      {
        method: 'POST',
        body: formData,
      },
    ).then((res) => res.json());
  };

const usePostVedleggAwaitAndDispatchResponse = (): ((
  avtaleId: string,
  vedleggId: string,
  type: PraksisavtaleVedleggTypeDto,
  fileFromFrontend: FileFromFrontend,
) => Promise<void>) => {
  const postVedlegg = usePostVedleggRequest();
  const dispatchVedleggResponse =
    useDispatchVedleggResponseAndUpdateInitState();

  const postRequest = async (
    avtaleId: string,
    vedleggId: string,
    type: PraksisavtaleVedleggTypeDto,
    fileFromFrontend: FileFromFrontend,
  ): Promise<void> => {
    const newVedlegg = await postVedlegg(avtaleId, type, fileFromFrontend);

    if (!responseIsUHPraksisavtaleVedleggMinimalDto(newVedlegg)) {
      throw Error('Response is not UHPraksisavtaleVedleggMinimalDto');
    }

    dispatchVedleggResponse({
      prevId: vedleggId,
      newVedlegg,
    });
  };

  return (...params): Promise<void> => postRequest(...params);
};

const useDeleteVedleggRequest = (): ((
  vedleggForDeletion: VedleggForDeletion,
) => Promise<void>) => {
  const { avtaleApi } = useContext(StudiestedApiContext);

  return (vedleggForDeletion: VedleggForDeletion) =>
    avtaleApi.avtalerPraksisAvtaleIdVedleggVedleggIdDelete({
      pathParams: {
        avtaleId: vedleggForDeletion.avtaleId,
        vedleggId: vedleggForDeletion.vedleggId,
      },
    });
};

const getUpdatedAvtaleVedleggFromResponse = (
  vedleggResponse: VedleggResponse,
  prevState: Avtalevedlegg,
): Avtalevedlegg => {
  const responseIsSamarbeidsavtale =
    vedleggResponse.newVedlegg.type ===
    PraksisavtaleVedleggTypeDto.Samarbeidsavtale;
  const responseIsDatabehandleravtale =
    vedleggResponse.newVedlegg.type ===
    PraksisavtaleVedleggTypeDto.Databehandleravtale;
  const responseIsAnnet =
    vedleggResponse.newVedlegg.type === PraksisavtaleVedleggTypeDto.Annet;

  return {
    samarbeidsavtale: responseIsSamarbeidsavtale
      ? mapToVedlegg(
          vedleggResponse.newVedlegg,
          PraksisavtaleVedleggTypeDto.Samarbeidsavtale,
        )
      : prevState.samarbeidsavtale,
    databehandleravtale: responseIsDatabehandleravtale
      ? mapToVedlegg(
          vedleggResponse.newVedlegg,
          PraksisavtaleVedleggTypeDto.Databehandleravtale,
        )
      : prevState.databehandleravtale,
    annet: responseIsAnnet
      ? prevState.annet.reduce(
          (
            acc: Vedlegg<typeof PraksisavtaleVedleggTypeDto.Annet>[],
            prevVedlegg: Vedlegg<typeof PraksisavtaleVedleggTypeDto.Annet>,
          ) => {
            if (vedleggResponse.prevId === prevVedlegg.id) {
              const { newVedlegg } = vedleggResponse;

              return [
                ...acc,
                {
                  ...prevVedlegg,
                  id: newVedlegg.id,
                  vedleggFile: {
                    fileFromBackend: { minimal: newVedlegg },
                  },
                },
              ];
            }

            return [...acc, prevVedlegg];
          },
          [],
        )
      : prevState.annet,
  };
};

const usePostThenDeleteRequestAndDispatchResponse = (): ((
  avtaleId: string,
  vedleggId: string,
  type: PraksisavtaleVedleggTypeDto,
  fileFromFrontend: FileFromFrontend,
) => Promise<void>) => {
  const postVedlegg = usePostVedleggRequest();
  const deleteVedlegg = useDeleteVedleggRequest();
  const dispatchVedleggResponse =
    useDispatchVedleggResponseAndUpdateInitState();

  const postThenDeleteRequest = async (
    avtaleId: string,
    vedleggId: string,
    type: PraksisavtaleVedleggTypeDto,
    fileFromFrontend: FileFromFrontend,
  ): Promise<void> => {
    const newVedlegg = await postVedlegg(avtaleId, type, fileFromFrontend);
    await deleteVedlegg({ avtaleId, vedleggId });

    if (!responseIsUHPraksisavtaleVedleggMinimalDto(newVedlegg)) {
      throw Error('Response is not UHPraksisavtaleVedleggMinimalDto');
    }

    dispatchVedleggResponse({
      prevId: vedleggId,
      newVedlegg,
    });
  };

  return (...params) => postThenDeleteRequest(...params);
};

const useDispatchVedleggResponseAndUpdateInitState = (): ((
  vedleggResponse: VedleggResponse,
) => void) => {
  const { dispatchAgreementEdit, vedlegg } = useAgreementContext();

  return (vedleggResponse: VedleggResponse) =>
    dispatchAgreementEdit({
      type: AgreementStateActionType.SET_VEDLEGG_AND_INIT_VEDLEGG,
      payload: getUpdatedAvtaleVedleggFromResponse(vedleggResponse, vedlegg),
    });
};

const useDeleteAnnetVedleggRequestThenDispatchUpdate = (): ((
  vedleggForDeletion: VedleggForDeletion,
) => Promise<void>) => {
  const { dispatchAgreementEdit, vedlegg } = useAgreementContext();
  const deleteRequest = useDeleteVedleggRequest();

  return (vedleggForDeletion: VedleggForDeletion) =>
    deleteRequest(vedleggForDeletion).then(() => {
      dispatchAgreementEdit({
        type: AgreementStateActionType.SET_VEDLEGG_AND_INIT_VEDLEGG,
        payload: {
          ...vedlegg,
          annet: vedlegg.annet.filter(
            (annetVedlegg) => annetVedlegg.id !== vedleggForDeletion.vedleggId,
          ),
        },
      });
    });
};

export const usePostVedleggUpdates = (): ((
  avtaleId: string,
  initAvtaleVedlegg: Avtalevedlegg,
  updatedAvtaleVedlegg: Avtalevedlegg,
) => Promise<Awaited<VedleggResponse | void>[]>) => {
  const postRequest = usePostVedleggAwaitAndDispatchResponse();
  const deleteRequest = useDeleteAnnetVedleggRequestThenDispatchUpdate();
  const postThenDeleteRequest = usePostThenDeleteRequestAndDispatchResponse();

  return (
    avtaleId: string,
    initAvtaleVedlegg: Avtalevedlegg,
    currentAvtaleVedlegg: Avtalevedlegg,
  ): Promise<Awaited<VedleggResponse | void>[]> => {
    const { annet: currentAnnet } = currentAvtaleVedlegg;
    const allCurrentFiles = flattenVedlegg(currentAvtaleVedlegg);

    const vedleggForPost = allCurrentFiles.filter((vedlegg) => {
      const { fileFromBackend, fileFromFrontend } = vedlegg.vedleggFile;
      const hasFileFromFrontend = !!fileFromFrontend;
      const isNew = hasFileFromFrontend && !fileFromBackend;
      const doesNotNeedManualDeletion =
        vedlegg.type !== PraksisavtaleVedleggTypeDto.Annet;

      return isNew || (hasFileFromFrontend && doesNotNeedManualDeletion);
    });

    const vedleggForPostAndDeletion = currentAnnet.filter((vedlegg) => {
      const { fileFromFrontend, fileFromBackend } = vedlegg.vedleggFile;
      return fileFromFrontend && fileFromBackend;
    });

    const vedleggForDeletion = filterInitVedleggForDeletion(
      avtaleId,
      initAvtaleVedlegg,
      currentAvtaleVedlegg,
    );

    return Promise.all([
      ...vedleggForPost.map((vedlegg) =>
        postRequest(
          avtaleId,
          vedlegg.id,
          vedlegg.type,
          vedlegg.vedleggFile.fileFromFrontend!, // Null-check done by filter
        ),
      ),
      ...vedleggForPostAndDeletion.map((vedlegg) =>
        postThenDeleteRequest(
          avtaleId,
          vedlegg.id,
          vedlegg.type,
          vedlegg.vedleggFile.fileFromFrontend!, // Null-check done by filter
        ),
      ),
      ...vedleggForDeletion.map(deleteRequest),
    ]);
  };
};
